import { MIN_PICKS, MIN_TOP } from './data'
import type { SpendCheckState } from '../types'

/** Same cookie convention as career-break / lead magnets. */
export const UNLOCK_COOKIE = 'worthwhile-spend-check_unlocked'

const STATE_KEY = 'worthwhile-spend-check_state'

/** Cache for useSyncExternalStore: getSnapshot must return Object.is-stable values. */
let snapshotCache: { raw: string | null; state: SpendCheckState | null } | null = null

function invalidateSnapshotCache(): void {
  snapshotCache = null
}

function parseUnlockedState(raw: string | null): SpendCheckState | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as SpendCheckState
    if (!Array.isArray(parsed.imp) || parsed.imp.length < MIN_PICKS) return null
    if (!Array.isArray(parsed.top) || parsed.top.length < MIN_TOP) return null
    return { ...parsed, screen: 'result' }
  } catch {
    return null
  }
}

export function hasUnlockCookie(): boolean {
  if (typeof document === 'undefined') return false
  return document.cookie.split('; ').some((c) => c.startsWith(`${UNLOCK_COOKIE}=`))
}

export function setUnlockCookie(): void {
  document.cookie = `${UNLOCK_COOKIE}=1; path=/; SameSite=Lax`
  invalidateSnapshotCache()
}

export function clearUnlockCookie(): void {
  document.cookie = `${UNLOCK_COOKIE}=; path=/; Max-Age=0; SameSite=Lax`
  invalidateSnapshotCache()
}

export function saveUnlockedState(state: SpendCheckState): void {
  try {
    sessionStorage.setItem(STATE_KEY, JSON.stringify({ ...state, screen: 'result' }))
  } catch {
    // Storage can be blocked — cookie still prevents a second lead submit UI path.
  }
  invalidateSnapshotCache()
}

/**
 * Stable unlock snapshot for `useSyncExternalStore`.
 * Parsing sessionStorage on every read allocates a new object and trips React #185.
 */
export function getUnlockedSnapshot(): SpendCheckState | null {
  if (!hasUnlockCookie()) {
    invalidateSnapshotCache()
    return null
  }

  let raw: string | null
  try {
    raw = sessionStorage.getItem(STATE_KEY)
  } catch {
    invalidateSnapshotCache()
    return null
  }

  if (snapshotCache && snapshotCache.raw === raw) return snapshotCache.state

  const state = parseUnlockedState(raw)
  snapshotCache = { raw, state }
  return state
}

export function loadUnlockedState(): SpendCheckState | null {
  return getUnlockedSnapshot()
}

export function clearUnlockedState(): void {
  try {
    sessionStorage.removeItem(STATE_KEY)
  } catch {
    // ignore
  }
  invalidateSnapshotCache()
}
